import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Eye, EyeOff, Lock, Loader2, Home, PartyPopper } from 'lucide-react'
import { AuthShell } from '@/components/AuthShell'
import LogoSpinner from '@/components/ui/LogoSpinner'
import { apiService, type PublicFamilyInvite } from '@/services/apiService'
import { useAuthStore } from '@/stores/authStore'

const relLabel: Record<string, string> = {
  SPOUSE: 'spouse',
  CHILD: 'child',
  PARENT: 'parent',
  SIBLING: 'sibling',
  OTHER: 'family member',
}

/**
 * Public invite-acceptance page (/family-invite/:token). A family member sets
 * a password and is connected to the inviter's cottage as a non-shareholder
 * household member. If they already have an account, the accept call links it
 * and we send them to sign in.
 */
const AcceptInvitePage = () => {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { signInWithPassword } = useAuthStore()

  const [loading, setLoading] = useState(true)
  const [invite, setInvite] = useState<PublicFamilyInvite | null>(null)
  const [loadError, setLoadError] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [linkedExisting, setLinkedExisting] = useState<{ hasPassword: boolean } | null>(null)

  useEffect(() => {
    let cancelled = false
    apiService
      .getFamilyInviteByToken(token)
      .then((d) => !cancelled && setInvite(d))
      .catch(
        (err) =>
          !cancelled &&
          setLoadError(
            err?.response?.data?.error?.message || 'This invite link is no longer valid.',
          ),
      )
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [token])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      const res = await apiService.acceptFamilyInvite(token, password)
      if (res.existingAccount) {
        setLinkedExisting({ hasPassword: res.hasPassword })
        setSubmitting(false)
      } else {
        await signInWithPassword(res.email, password)
        navigate('/dashboard', { replace: true })
      }
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-deepwater">
        <LogoSpinner size="lg" label="Loading your invite" />
      </div>
    )
  }

  // Invalid / expired / revoked / already-accepted.
  if (loadError || !invite || !invite.valid) {
    const status = invite?.status
    const msg =
      status === 'ACCEPTED'
        ? 'This invite has already been accepted. If that was you, just sign in.'
        : status === 'EXPIRED'
          ? 'This invite has expired. Please ask your family member to send a new invite.'
          : status === 'REVOKED'
            ? 'This invite is no longer valid.'
            : loadError || 'This invite link is no longer valid.'
    return (
      <AuthShell title="Invite unavailable" subtitle="We couldn't open this invitation" backTo="/login" backLabel="Go to sign in">
        <div className="text-center">
          <p className="text-blue-50 text-sm leading-relaxed">{msg}</p>
          <Link
            to="/login"
            className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 font-semibold text-blue-900 shadow-lg transition hover:bg-blue-50"
          >
            Go to sign in
          </Link>
        </div>
      </AuthShell>
    )
  }

  // Existing account was linked — send them to sign in.
  if (linkedExisting) {
    return (
      <AuthShell title="You're connected!" subtitle={`Cottage #${invite.cottageNumber ?? ''}`} backTo="/login" backLabel="Go to sign in">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-white/20 bg-white/10">
            <PartyPopper size={26} className="text-gold" />
          </div>
          <p className="text-blue-50 text-sm leading-relaxed">
            <span className="font-semibold">{invite.invitedEmail}</span> already has a Shorewood
            account, and it's now connected to Cottage #{invite.cottageNumber}. Sign in to continue.
          </p>
          {!linkedExisting.hasPassword && (
            <p className="mt-3 text-blue-100/70 text-xs">
              Haven't set a password yet? Use “Forgot your password” on the sign-in page.
            </p>
          )}
          <Link
            to="/login"
            className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 font-semibold text-blue-900 shadow-lg transition hover:bg-blue-50"
          >
            Go to sign in
          </Link>
        </div>
      </AuthShell>
    )
  }

  // Valid pending invite — create an account.
  const relationship = relLabel[invite.relationship] ?? 'family member'
  return (
    <AuthShell
      title="Join the Shorewood portal"
      subtitle={`Cottage #${invite.cottageNumber ?? ''}`}
      backTo="/login"
      backLabel="Already have an account? Sign in"
    >
      <div className="mb-5 rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm leading-relaxed text-blue-50">
        <span className="font-semibold">{invite.inviterName}</span> invited you (their {relationship})
        to join the Shorewood member portal for{' '}
        <span className="font-semibold">Cottage #{invite.cottageNumber}</span>. Create your login to
        access community updates, documents, events, photos, and other member resources.
        {invite.message && (
          <span className="mt-2 block border-l-2 border-gold/50 pl-3 italic text-blue-100/90">
            “{invite.message}”
          </span>
        )}
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <span className="mb-2 block text-sm font-medium text-white">Your email</span>
          <div className="flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-3 text-sm text-blue-50">
            <Home size={16} className="text-blue-200" />
            {invite.invitedEmail}
          </div>
        </div>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-white">Create a password</span>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              required
              autoFocus
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (error) setError('')
              }}
              placeholder="At least 8 characters"
              className="w-full rounded-lg border border-gray-200 bg-white/95 py-3 pl-10 pr-11 text-gray-900 placeholder-gray-400 input-focus"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-white">Confirm password</span>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value)
                if (error) setError('')
              }}
              placeholder="Re-enter your password"
              className="w-full rounded-lg border border-gray-200 bg-white/95 py-3 pl-10 pr-11 text-gray-900 placeholder-gray-400 input-focus"
            />
          </div>
        </label>

        {error && (
          <div className="rounded-lg border border-red-300/40 bg-red-500/15 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-blue-900 shadow-lg transition hover:bg-blue-50 disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating your account…
            </>
          ) : (
            'Create account & join'
          )}
        </button>
      </form>
    </AuthShell>
  )
}

export default AcceptInvitePage
